// Backup — local archive (list/create/restore/download/schedule) + remote
// destinations (FTP/SFTP/SMB/S3 via rclone). Backed by /api/backup/* and
// /api/backup/destinations/*.

import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, Download, Trash2, Play, RefreshCw, Calendar, Upload, RotateCw, FileUp,
         Cloud, Plus, Pencil, Wifi, UploadCloud, FolderOpen, X } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function Backup() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [destEditor, setDestEditor] = useState<any | null>(null);

  const list = useQuery<any>({ queryKey: ['backup-list'], queryFn: () => fJson('/api/backup/list'), refetchInterval: 5000 });
  const status = useQuery<any>({ queryKey: ['backup-status'], queryFn: () => fJson('/api/backup/status'), refetchInterval: 1500 });
  const schedule = useQuery<any>({ queryKey: ['backup-schedule'], queryFn: () => fJson('/api/backup/schedule') });
  const dests = useQuery<any[]>({ queryKey: ['backup-dests'], queryFn: () => fJson('/api/backup/destinations'), refetchInterval: 10000 });
  const refreshDests = () => qc.invalidateQueries({ queryKey: ['backup-dests'] });

  const create = useMutation({
    mutationFn: () => fJson('/api/backup/create', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backup-list'] }),
  });
  const del = useMutation({
    mutationFn: (filename: string) => fJson(`/api/backup/${encodeURIComponent(filename)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backup-list'] }),
  });
  const restore = useMutation({
    mutationFn: async (filename: string) => {
      const post = (body: any) => fetch('/api/backup/restore', { method: 'POST', headers: { ...auth().headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      let r = await post({ filename });
      if (r.status === 428) {
        // Legacy unsigned archive — the server refuses without an explicit override.
        if (!confirm(`${filename} has no integrity signature (created before backup signing was enabled). Its contents cannot be verified. Restore anyway?`)) throw new Error('Restore cancelled.');
        r = await post({ filename, allow_unsigned: true });
      }
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => alert('Restore started — check Status panel for progress.'),
    onError: (e: any) => alert(String(e?.message || e)),
  });
  const saveSchedule = useMutation({
    mutationFn: (b: any) => fJson('/api/backup/schedule', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backup-schedule'] }),
  });

  function uploadFile(file: File, andRestore: boolean) {
    const url = andRestore ? '/api/backup/upload-restore' : '/api/backup/upload';
    // An uploaded archive is unsigned from the server's point of view unless its .sig
    // is already present — restoring one requires the explicit unsigned override.
    if (andRestore && !confirm(`Restore directly from ${file.name}? Uploaded archives are only integrity-verified if a matching signature already exists on the server; otherwise this restores unverified content.`)) return;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploading(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      setUploading(0);
      if (xhr.status >= 200 && xhr.status < 300) {
        const r = JSON.parse(xhr.responseText || '{}');
        qc.invalidateQueries({ queryKey: ['backup-list'] });
        if (andRestore) alert(`Restore started from ${r.filename}.`);
        else alert(`Imported ${r.filename}. Click Restore on the row to apply.`);
      } else {
        alert(`Upload failed: ${xhr.responseText}`);
      }
    };
    xhr.onerror = () => { setUploading(0); alert('Upload failed: network error'); };
    const fd = new FormData();
    fd.append('backup', file);
    if (andRestore) fd.append('allow_unsigned', 'true');
    setUploading(1);
    xhr.send(fd);
  }

  const files: any[] = list.data?.files || list.data || [];
  const sched: any = schedule.data || {};
  const st = status.data || {};
  const isRunning = st.status === 'running';
  const stepPct = isRunning && st.step && st.total_steps ? Math.round((st.step / st.total_steps) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Database /> Backup</h1>
        <div className="flex gap-2">
          <button className="btn" onClick={() => list.refetch()} disabled={list.isFetching}>
            <RefreshCw size={14} className={list.isFetching ? 'animate-spin inline mr-1' : 'inline mr-1'} /> Refresh
          </button>
          <button className="btn-primary inline-flex items-center gap-2 px-4" onClick={() => create.mutate()} disabled={create.isPending || isRunning}>
            <Play size={16} /> {create.isPending || isRunning ? 'Running…' : 'Create Backup Now'}
          </button>
        </div>
      </div>

      {/* Big visible Import drop-zone */}
      <DropZone fileRef={fileRef} uploadFile={uploadFile} uploading={uploading} />

      {/* Live progress strip — animated indeterminate stripes plus % bar */}
      {(isRunning || st.status === 'error' || st.status === 'done') && (
        <div className={`card p-4 text-sm space-y-3 ${isRunning ? 'bg-warning/10 border border-warning' : st.status === 'error' ? 'bg-danger/10 border border-danger' : 'bg-success/10 border border-success'}`}>
          <div className="flex items-center justify-between">
            <span className="font-medium flex items-center gap-2">
              {isRunning && <RotateCw size={14} className="animate-spin" />}
              {isRunning ? `${st.job === 'restore' ? 'Restoring' : 'Backup'}: step ${st.step || 0} of ${st.total_steps || 8} — ${st.message || 'Working…'}`
                : st.status === 'error' ? `Last ${st.job} failed`
                : `Last ${st.job} completed`}
            </span>
            <span className="text-xs text-textSecondary">
              {st.started && new Date(st.started).toLocaleTimeString()}{st.finished && ` → ${new Date(st.finished).toLocaleTimeString()}`}
            </span>
          </div>
          {isRunning && (
            <>
              <div className="relative h-3 bg-elevated rounded-full overflow-hidden">
                <div className="bg-primary h-full transition-all duration-700 ease-out progress-striped"
                     style={{ width: `${Math.max(stepPct, 5)}%` }} />
              </div>
              <div className="h-0.5 bg-elevated rounded-full overflow-hidden">
                <div className="h-full text-primary progress-pulse" />
              </div>
              <div className="text-xs flex justify-between text-textSecondary">
                <span>{stepPct}% complete</span>
                <span>{elapsed(st.started)} elapsed</span>
              </div>
            </>
          )}
          {st.status === 'error' && <pre className="text-xs whitespace-pre-wrap text-danger">{st.error}</pre>}
          {st.status === 'done' && st.filename && <div className="text-xs text-success">Saved: <span className="font-mono">{st.filename}</span></div>}
        </div>
      )}

      {uploading > 0 && (
        <div className="card p-4 text-sm bg-primary/10 border border-primary space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium">Uploading backup… {uploading}%</span>
            <span className="text-xs text-textSecondary">XHR streaming</span>
          </div>
          <div className="relative h-3 bg-elevated rounded-full overflow-hidden">
            <div className="bg-primary h-full transition-all duration-200 progress-striped" style={{ width: `${uploading}%` }} />
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Local Backups</th><th>Size</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {files.map((f: any) => {
              const fname = f.filename || f.name;
              return (
                <tr key={fname} className="hover:bg-elevated">
                  <td className="font-mono text-sm">{fname}</td>
                  <td className="text-sm">{f.size ? formatBytes(f.size) : '—'}</td>
                  <td className="text-xs">{(f.mtime || f.date) ? new Date(f.mtime || f.date).toLocaleString() : '—'}</td>
                  <td className="space-x-2 whitespace-nowrap text-right">
                    <a className="btn inline-flex items-center gap-1 text-xs"
                       href={`/api/backup/download/${encodeURIComponent(fname)}?token=${getToken()}`}
                       target="_blank" rel="noreferrer" title="Download (.zip)">
                      <Download size={12} /> Download
                    </a>
                    <button className="btn inline-flex items-center gap-1 text-xs"
                            onClick={() => confirm(`Restore from ${fname}?\n\nThis will OVERWRITE the current FusionPBX database, FreeSWITCH config, and Stratus data with what's in this backup.`) && restore.mutate(fname)}
                            title="Restore from this backup">
                      <RotateCw size={12} /> Restore
                    </button>
                    <button className="btn-ghost p-1 text-danger inline-flex"
                            onClick={() => confirm(`Delete ${fname}?`) && del.mutate(fname)}
                            title="Delete"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
            {files.length === 0 && (
              <tr><td colSpan={4} className="text-center py-8 text-textSecondary">
                No backups yet. Click <strong>Create Backup Now</strong> to take the first one, or <strong>Import Backup</strong> to upload one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card p-4 space-y-3">
        <h2 className="font-semibold flex items-center gap-2"><Calendar size={16}/> Scheduled Backups</h2>
        <ScheduleEditor schedule={sched} onSave={(b) => saveSchedule.mutate(b)} />
      </div>

      {/* ── Remote Destinations ─────────────────────────────────────────── */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2"><Cloud size={16}/> Remote Destinations</h2>
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setDestEditor({})}>
            <Plus size={14} /> Add Destination
          </button>
        </div>
        <p className="text-sm text-textSecondary">
          Every completed local backup is automatically copied to each <strong>enabled</strong> destination
          (FTP / SFTP / SMB / S3·MinIO). Each destination keeps its newest <em>retention</em> copies and has its own controls.
        </p>
        {(dests.data || []).length === 0 && (
          <div className="text-sm text-textSecondary py-3">No remote destinations yet — add one to keep off-box copies of your backups.</div>
        )}
        <div className="grid gap-3">
          {(dests.data || []).map((d) => (
            <DestinationCard key={d.id} dest={d} onEdit={() => setDestEditor(d)} onChanged={refreshDests} />
          ))}
        </div>
      </div>

      {destEditor && (
        <DestinationEditor
          dest={destEditor}
          onClose={() => setDestEditor(null)}
          onSaved={() => { setDestEditor(null); refreshDests(); }}
        />
      )}
    </div>
  );
}

// ── Destination card: status + per-destination controls ────────────────────────
function DestinationCard({ dest, onEdit, onChanged }: { dest: any; onEdit: () => void; onChanged: () => void }) {
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [files, setFiles] = useState<any[] | null>(null);
  const c = dest.config || {};
  const base = `/api/backup/destinations/${dest.id}`;

  const summary =
    dest.type === 's3' ? `${c.endpoint || 'AWS'} · ${c.bucket || ''}/${c.path || ''}`
    : dest.type === 'smb' ? `\\\\${c.host}\\${c.share || ''}\\${c.path || ''}`
    : `${c.host || ''}${c.port ? ':' + c.port : ''} · ${c.path || '/'}`;

  async function call(p: string, init: RequestInit, label: string) {
    setBusy(label); setMsg('');
    try { return await fJson(base + p, init); }
    catch (e: any) { setMsg('✗ ' + (e.message || 'failed')); throw e; }
    finally { setBusy(''); }
  }
  const test = async () => { try { const r = await call('/test', { method: 'POST' }, 'test'); setMsg(r.ok ? '✓ Connection OK' : '✗ ' + (r.error || 'failed')); onChanged(); } catch {} };
  const backupNow = async () => { try { const r = await call('/backup-now', { method: 'POST' }, 'backup'); setMsg(`✓ Uploaded ${r.uploaded || ''}${r.pruned ? ` (pruned ${r.pruned})` : ''}`); onChanged(); } catch {} };
  const showFiles = async () => { if (files) { setFiles(null); return; } try { setFiles(await call('/files', {}, 'files')); } catch {} };
  const restoreFile = async (name: string) => { if (!confirm(`Restore ${name} from "${dest.name}"?\n\nThis pulls it down and OVERWRITES current data.`)) return; try { await call('/restore', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) }, 'restore'); setMsg('Restore started — see the Status panel up top.'); } catch {} };
  const delFile = async (name: string) => { if (!confirm(`Delete ${name} from "${dest.name}"?`)) return; try { await call(`/files/${encodeURIComponent(name)}`, { method: 'DELETE' }, 'delfile'); setFiles(await fJson(base + '/files')); } catch {} };
  const toggle = async () => { try { await fJson(base, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !dest.enabled }) }); onChanged(); } catch (e: any) { setMsg('✗ ' + e.message); } };
  const remove = async () => { if (!confirm(`Delete destination "${dest.name}"?\n\n(Backups already stored there are NOT removed.)`)) return; try { await fJson(base, { method: 'DELETE' }); onChanged(); } catch (e: any) { setMsg('✗ ' + e.message); } };

  const stColor = dest.last_status === 'ok' || dest.last_status === 'test_ok' ? 'text-success'
    : (dest.last_status === 'error' || dest.last_status === 'test_failed') ? 'text-danger' : 'text-textSecondary';

  return (
    <div className={`card p-4 space-y-2 border ${dest.enabled ? 'border-border' : 'border-border opacity-60'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/15 text-primary">{String(dest.type).toUpperCase()}</span>
          <span className="font-semibold truncate">{dest.name}</span>
          {!dest.enabled && <span className="text-xs text-textSecondary">(disabled)</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button className="btn text-xs inline-flex items-center gap-1" onClick={test} disabled={!!busy} title="Test connection"><Wifi size={12}/>{busy === 'test' ? '…' : 'Test'}</button>
          <button className="btn text-xs inline-flex items-center gap-1" onClick={backupNow} disabled={!!busy} title="Push the newest local backup now"><UploadCloud size={12}/>{busy === 'backup' ? '…' : 'Backup now'}</button>
          <button className="btn text-xs inline-flex items-center gap-1" onClick={showFiles} disabled={!!busy} title="List backups stored here"><FolderOpen size={12}/>Files</button>
          <button className="btn-ghost p-1" onClick={onEdit} title="Edit"><Pencil size={14}/></button>
          <button className="btn-ghost p-1" onClick={toggle} title={dest.enabled ? 'Disable' : 'Enable'}><span className="text-xs">{dest.enabled ? 'On' : 'Off'}</span></button>
          <button className="btn-ghost p-1 text-danger" onClick={remove} title="Delete destination"><Trash2 size={14}/></button>
        </div>
      </div>
      <div className="text-xs text-textSecondary font-mono truncate">{summary}</div>
      <div className="text-xs flex flex-wrap gap-x-4 gap-y-1">
        <span className={stColor}>● {dest.last_status || 'never run'}</span>
        {dest.last_run && <span className="text-textSecondary">last run {new Date(dest.last_run).toLocaleString()}</span>}
        <span className="text-textSecondary">keep {dest.retention} copies</span>
      </div>
      {dest.last_error && dest.last_status === 'error' && <div className="text-xs text-danger">{dest.last_error}</div>}
      {msg && <div className={`text-xs ${msg.startsWith('✓') ? 'text-success' : 'text-danger'}`}>{msg}</div>}
      {files && (
        <div className="border-t border-border pt-2 mt-1 space-y-1">
          {files.length === 0 && <div className="text-xs text-textSecondary">No backups stored here yet.</div>}
          {files.map((f) => (
            <div key={f.name} className="flex items-center justify-between text-xs">
              <span className="font-mono truncate">{f.name}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="text-textSecondary">{formatBytes(f.size)}</span>
                <button className="btn-ghost px-1 inline-flex items-center gap-1" onClick={() => restoreFile(f.name)} title="Pull + restore"><RotateCw size={11}/>Restore</button>
                <button className="btn-ghost px-1 text-danger" onClick={() => delFile(f.name)} title="Delete remote copy"><Trash2 size={12}/></button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Add / edit destination modal ───────────────────────────────────────────────
const DEST_TYPES = [
  { v: 'ftp', label: 'FTP' },
  { v: 'sftp', label: 'SFTP' },
  { v: 'smb', label: 'SMB / CIFS' },
  { v: 's3', label: 'S3 / MinIO' },
];

function DestinationEditor({ dest, onClose, onSaved }: { dest: any; onClose: () => void; onSaved: () => void }) {
  const isNew = !dest.id;
  const [f, setF] = useState<any>(() => ({
    name: dest.name || '', type: dest.type || 'ftp', enabled: dest.enabled !== false,
    retention: dest.retention ?? 10, config: { ...(dest.config || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const c = f.config || {};
  const setC = (k: string, v: any) => setF({ ...f, config: { ...f.config, [k]: v } });
  const set = (k: string, v: any) => setF({ ...f, [k]: v });
  const passPH = c.has_pass ? '•••••• (leave blank to keep)' : '';

  async function save() {
    setSaving(true); setErr('');
    const config: any = { ...f.config };
    if (!config.pass) delete config.pass;
    if (!config.secret_access_key) delete config.secret_access_key;
    delete config.has_pass; delete config.has_secret_key;
    const payload = { name: f.name, type: f.type, enabled: f.enabled, retention: f.retention, config };
    try {
      await fJson(isNew ? '/api/backup/destinations' : `/api/backup/destinations/${dest.id}`,
        { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      onSaved();
    } catch (e: any) { setErr(e.message || 'save failed'); } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">{isNew ? 'Add' : 'Edit'} Destination</h3>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16}/></button>
        </div>

        <Field label="Name"><input className="input" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Offsite NAS" /></Field>
        <Field label="Type">
          <select className="input" value={f.type} onChange={(e) => set('type', e.target.value)} disabled={!isNew}>
            {DEST_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </Field>

        {(f.type === 'ftp' || f.type === 'sftp') && (<>
          <Row>
            <Field label="Host"><input className="input" value={c.host || ''} onChange={(e) => setC('host', e.target.value)} /></Field>
            <Field label="Port"><input className="input" value={c.port || ''} onChange={(e) => setC('port', e.target.value)} placeholder={f.type === 'ftp' ? '21' : '22'} /></Field>
          </Row>
          <Field label="Username"><input className="input" value={c.user || ''} onChange={(e) => setC('user', e.target.value)} /></Field>
          <Field label="Password"><input className="input" type="password" value={c.pass || ''} onChange={(e) => setC('pass', e.target.value)} placeholder={passPH} /></Field>
          <Field label="Remote path"><input className="input" value={c.path || ''} onChange={(e) => setC('path', e.target.value)} placeholder="/backups/stratus" /></Field>
          {f.type === 'ftp' && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!c.tls} onChange={(e) => setC('tls', e.target.checked)} /> Use explicit TLS (FTPS)</label>}
        </>)}

        {f.type === 'smb' && (<>
          <Field label="Host / server"><input className="input" value={c.host || ''} onChange={(e) => setC('host', e.target.value)} placeholder="192.168.1.10" /></Field>
          <Row>
            <Field label="Username"><input className="input" value={c.user || ''} onChange={(e) => setC('user', e.target.value)} /></Field>
            <Field label="Domain (optional)"><input className="input" value={c.domain || ''} onChange={(e) => setC('domain', e.target.value)} /></Field>
          </Row>
          <Field label="Password"><input className="input" type="password" value={c.pass || ''} onChange={(e) => setC('pass', e.target.value)} placeholder={passPH} /></Field>
          <Row>
            <Field label="Share"><input className="input" value={c.share || ''} onChange={(e) => setC('share', e.target.value)} placeholder="backups" /></Field>
            <Field label="Path in share"><input className="input" value={c.path || ''} onChange={(e) => setC('path', e.target.value)} placeholder="stratus" /></Field>
          </Row>
        </>)}

        {f.type === 's3' && (<>
          <Row>
            <Field label="Provider"><input className="input" value={c.provider || 'Minio'} onChange={(e) => setC('provider', e.target.value)} placeholder="Minio / AWS / Wasabi…" /></Field>
            <Field label="Endpoint (blank = AWS)"><input className="input" value={c.endpoint || ''} onChange={(e) => setC('endpoint', e.target.value)} placeholder="http://host:9000" /></Field>
          </Row>
          <Row>
            <Field label="Bucket"><input className="input" value={c.bucket || ''} onChange={(e) => setC('bucket', e.target.value)} /></Field>
            <Field label="Path / prefix"><input className="input" value={c.path || ''} onChange={(e) => setC('path', e.target.value)} placeholder="stratus" /></Field>
          </Row>
          <Field label="Access key ID"><input className="input" value={c.access_key_id || ''} onChange={(e) => setC('access_key_id', e.target.value)} /></Field>
          <Field label="Secret access key"><input className="input" type="password" value={c.secret_access_key || ''} onChange={(e) => setC('secret_access_key', e.target.value)} placeholder={c.has_secret_key ? '•••••• (leave blank to keep)' : ''} /></Field>
          <Field label="Region (optional)"><input className="input" value={c.region || ''} onChange={(e) => setC('region', e.target.value)} placeholder="us-east-1" /></Field>
        </>)}

        <Row>
          <Field label="Retention (copies to keep)"><input className="input" type="number" min={1} value={f.retention} onChange={(e) => set('retention', parseInt(e.target.value, 10) || 10)} /></Field>
          <Field label=" "><label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Enabled (receive backups)</label></Field>
        </Row>

        {err && <div className="text-danger text-xs">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving || !f.name}>{saving ? 'Saving…' : (isNew ? 'Add' : 'Save')}</button>
        </div>
      </div>
    </div>
  );
}

function ScheduleEditor({ schedule, onSave }: { schedule: any; onSave: (b: any) => void }) {
  const [form, setForm] = useState<any>({});
  const f = { ...(schedule || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />
        Enable scheduled backups
      </label>
      <div className="grid grid-cols-4 gap-2">
        <Field label="Frequency">
          <select className="input" value={f.frequency || '3days'} onChange={e => set('frequency', e.target.value)} disabled={!f.enabled}>
            <option value="1day">Daily</option>
            <option value="3days">Every 3 days</option>
            <option value="1week">Weekly</option>
            <option value="1month">Monthly</option>
          </select>
        </Field>
        <Field label="Hour (0–23)">
          <input className="input" type="number" min={0} max={23} value={f.hour ?? 2} onChange={e => set('hour', parseInt(e.target.value, 10))} disabled={!f.enabled} />
        </Field>
        <Field label="Minute (0–59)">
          <input className="input" type="number" min={0} max={59} value={f.minute ?? 0} onChange={e => set('minute', parseInt(e.target.value, 10))} disabled={!f.enabled} />
        </Field>
        <Field label="Retain (copies)">
          <input className="input" type="number" min={1} value={f.retention ?? 10} onChange={e => set('retention', parseInt(e.target.value, 10))} disabled={!f.enabled} />
        </Field>
      </div>
      <button className="btn-primary" onClick={() => onSave({ enabled: !!f.enabled, frequency: f.frequency || '3days', hour: f.hour ?? 2, minute: f.minute ?? 0, retention: f.retention ?? 10 })}>Save Schedule</button>
      <p className="text-xs text-textSecondary">Each scheduled (or manual) backup is also copied to every enabled remote destination above.</p>
    </div>
  );
}

function DropZone({ fileRef, uploadFile, uploading }: { fileRef: any; uploadFile: (f: File, r: boolean) => void; uploading: number }) {
  const [drag, setDrag] = useState(false);
  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) { alert('Backup must be a .zip file'); return; }
    const restore = confirm(`Imported ${f.name}.\n\nOK = restore immediately (overwrites current data).\nCancel = save to backup list — restore later.`);
    uploadFile(f, restore);
  }
  function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return;
    const restore = confirm(`Imported ${f.name}.\n\nOK = restore immediately (overwrites current data).\nCancel = save to backup list — restore later.`);
    uploadFile(f, restore);
    e.target.value = '';
  }
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      className={`card p-6 border-2 border-dashed transition-colors ${drag ? 'border-primary bg-primary/10' : 'border-border'}`}
    >
      <input ref={fileRef} type="file" accept=".zip" hidden onChange={pick} />
      <div className="flex items-center gap-4">
        <div className="rounded-full bg-primary/15 p-3 text-primary"><FileUp size={28} /></div>
        <div className="flex-1">
          <div className="font-semibold text-base">Import Backup</div>
          <div className="text-sm text-textSecondary">Drag a <code className="bg-elevated px-1 rounded">.zip</code> file here or click <strong>Browse</strong>. After upload you can choose to save it to the list or restore immediately.</div>
        </div>
        <button className="btn-primary inline-flex items-center gap-2 px-4 py-2"
                onClick={() => fileRef.current?.click()} disabled={uploading > 0}>
          <Upload size={16} /> {uploading > 0 ? `Uploading ${uploading}%` : 'Browse…'}
        </button>
      </div>
    </div>
  );
}

function elapsed(startIso: string | undefined): string {
  if (!startIso) return '0s';
  const ms = Date.now() - new Date(startIso).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60_000), s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><label className="text-sm">{label}</label>{children}</div>;
}
function formatBytes(b: number) {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
